import React from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import get from "lodash/get";
import { collectionToStories } from "@quintype/components";
import CollectionTitle from "../../atoms/CollectionTitle";
import { useDeviceType } from "../../custom-hooks/useDeviceType";
import { RowSeparator } from "../../atoms/RowSeparator";
import { DfpComponent } from "../../ads/dfp-component";
import { OtherStories } from "./OtherStories";
import { StoryCard } from "./StoryCard";

import "./three-col-nine-var-one.m.css";
export default function ThreeColumnNineStoriesVarOne({ collection, extraProp = "" }) {
  const associatedMetadata = get(collection, ["associated-metadata"], {});

  const rowAdUnitBottom = associatedMetadata.show_row_ad_unit_bottom || null;
  const showColumnSeparator = associatedMetadata.show_column_separator || null;
  const showRowSeparator = associatedMetadata.show_row_separator || null;
  const showTimeStamp = associatedMetadata.show_timestamp || null;
  const showReadTime = associatedMetadata.show_read_time || null;
  const showRowTitle = associatedMetadata.show_row_title || null;
  const enableSponsorAd = associatedMetadata.enable_sponsor_ad || null;
  const sponsorAdDetails = associatedMetadata.sponsor_ad_details || null;
  const showSubheadline = associatedMetadata.show_sub_headline || null;
  const showStorySeparator = associatedMetadata.show_story_separator || null;

  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";
  const sectionSlug = extraProp;

  const stories = collectionToStories(collection);
  const bottomAdConfig = useSelector((state) =>
    get(state, ["qt", "config", "ads-config", "slots", "row_bottom_ad"], {})
  );
  const sponsorAdConfig = useSelector((state) => get(state, ["qt", "config", "ads-config", "slots", "sponsor_ad"], {}));
  const pageType = useSelector((state) => get(state, ["qt", "pageType"], null));

  if (!stories.length) return null;

  const [firstStory, ...otherStories] = stories;
  const firstSet = isMobile ? otherStories : otherStories.slice(0, 4);
  const secondSet = otherStories.slice(4, 6);
  const thirdSet = otherStories.slice(6, 8);
  const combinedSet = [...secondSet, ...thirdSet];

  const getColStyle = (separatorRule, styleRule) => {
    const rule = isMobile ? showStorySeparator : separatorRule;
    const styleRuleWithSeparator = `${styleRule}-with-separator`;
    return rule ? styleRuleWithSeparator : styleRule;
  };

  const getColumn3 = (storySet) => {
    return (
      <div styleName={getColStyle(showStorySeparator, "column-three-stories")}>
        <OtherStories
          showStorySeparator={showStorySeparator}
          otherStories={storySet}
          showTimeStamp={showTimeStamp}
          showReadTime={showReadTime}
          sectionSlug={sectionSlug}
        />
      </div>
    );
  };

  const getColumn3ForTablet = () => {
    return (
      <div styleName={getColStyle(showColumnSeparator, "column-three-for-tablet")}>
        {getColumn3(secondSet)}
        {showColumnSeparator && thirdSet.length > 0 && <div styleName="divider"></div>}
        {getColumn3(thirdSet)}
      </div>
    );
  };

  return (
    <div className="row-component" styleName="top-component-wrapper">
      <div styleName="three-col-nine-var-one">
        {showRowTitle && (
          <CollectionTitle
            collection={collection}
            enableSponsorAd={enableSponsorAd}
            sponsorAdDetails={sponsorAdDetails}
            sectionSlug={sectionSlug}
            sponsorAdConfig={sponsorAdConfig}
          />
        )}
        <div styleName={getColStyle(showColumnSeparator, "stories-list")}>
          <div styleName="column-one">
            <StoryCard
              story={firstStory}
              showTimeStamp={showTimeStamp}
              showReadTime={showReadTime}
              isHeroStory={true}
              showSubheadline={showSubheadline}
              sectionSlug={sectionSlug}
            />
          </div>
          {isMobile
            ? showStorySeparator && otherStories.length > 0 && <div styleName="divider"></div>
            : showColumnSeparator && otherStories.length > 0 && <div styleName="divider"></div>}
          <div styleName={getColStyle(showStorySeparator, "column-two")}>
            <OtherStories
              otherStories={firstSet}
              showStorySeparator={showStorySeparator}
              showTimeStamp={showTimeStamp}
              showReadTime={showReadTime}
              isFirstSet={true}
              sectionSlug={sectionSlug}
            />
          </div>
          {isMobile || isTablet
            ? showStorySeparator && <div styleName="tablet-divider"></div>
            : showColumnSeparator && <div styleName="divider"></div>}
          {isDesktop && getColumn3(combinedSet)}
        </div>
        {isTablet && (
          <div styleName={getColStyle(showStorySeparator, "tablet-second-row")}> {getColumn3ForTablet()} </div>
        )}
      </div>
      {showRowSeparator && <RowSeparator />}
      {rowAdUnitBottom && (
        <DfpComponent
          adStyleName="ad-slot-size-300x250"
          id={`${pageType}-${collection.slug}-row-bottom-ad`}
          path={rowAdUnitBottom}
          size={bottomAdConfig.sizes}
          viewPortSizeMapping={bottomAdConfig.view_port_size_mapping}
        />
      )}
    </div>
  );
}

ThreeColumnNineStoriesVarOne.propTypes = {
  collection: PropTypes.shape({
    slug: PropTypes.string,
  }),
  stories: PropTypes.array.isRequired,
  config: PropTypes.object,
  extraProp: PropTypes.string,
};

ThreeColumnNineStoriesVarOne.storyLimit = 9;
